import axios from "axios";
import { logger } from "./logger";
import { enqueue, isNetworkError, QUEUEABLE_ENDPOINTS, labelForEndpoint } from "./offlineQueue";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth strategy: support BOTH httpOnly cookies (same-origin deployments) and
// Authorization: Bearer header (cross-origin / custom-domain deployments).
// The backend's get_current_user dependency accepts either.
const TOKEN_KEY = "tg_token";

export const tokenStore = {
  get: () => {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  },
  set: (t) => {
    try {
      localStorage.setItem(TOKEN_KEY, t);
    } catch (err) {
      logger.warn("Could not persist auth token:", err);
    }
  },
  clear: () => {
    try {
      localStorage.removeItem(TOKEN_KEY);
    } catch (err) {
      // Some browser modes (private, storage disabled) throw on access.
      // Failure here is benign — the token cookie is the real auth — but log for debugging.
      // eslint-disable-next-line no-console
      console.debug("token.clear: localStorage unavailable:", err?.message);
    }
  },
};

export const api = axios.create({
  baseURL: API,
});

api.interceptors.request.use((config) => {
  const t = tokenStore.get();
  if (t) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    if (err?.response?.status === 401) {
      logger.warn("API 401 — session expired or not authenticated");
      tokenStore.clear();
    }
    // Offline write queueing — only for the 3 field-critical write endpoints
    // and only when the failure looks like a real network problem (not a 4xx/5xx).
    const cfg = err?.config;
    const method = (cfg?.method || "").toLowerCase();
    const url = (cfg?.url || "").replace(/^\/+/, "/");
    if (method === "post" && QUEUEABLE_ENDPOINTS.has(url) && isNetworkError(err)) {
      try {
        const payload = typeof cfg.data === "string" ? JSON.parse(cfg.data) : (cfg.data || {});
        const entry = await enqueue({ endpoint: url, payload });
        // Return a synthetic axios response so callers see success + can move on.
        return {
          data: { queued: true, client_id: entry.id, ...payload },
          status: 202,
          statusText: "QUEUED_OFFLINE",
          headers: {},
          config: cfg,
        };
      } catch (qErr) {
        logger.warn(`offline queue failed for ${url}:`, qErr?.message);
      }
    }
    return Promise.reject(err);
  }
);

export { labelForEndpoint };

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
